import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import { NotificationService } from '@shared/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@modules/components/auth/auth.service';
import { i18n } from '@app/shared/language/i18n';
import { isOnPublicRoute } from '../utils/public-route.util';
import { SKIP_ERROR_NOTIFICATION } from './request.constants';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptor implements HttpInterceptor {
  /**
   * Inject storage
   */
  public constructor(
    private readonly translateService: TranslateService,
    public readonly notificationService: NotificationService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}
  /**
   * Set needed headers for api
   */
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (
      !request.url.includes('.svg') &&
      !request.url.includes('icons') &&
      !request.url.includes('assets')
    ) {
      request = request.clone({
        url: request.url,
      });
    }

    return next.handle(request).pipe(
      catchError((err) => {
        if (request.context.get(SKIP_ERROR_NOTIFICATION)) {
          return throwError(err);
        }
        if (request.url.startsWith(environment.PifagorApiAddress)) {
          if (err.status === 0 && !this.authService.jwtToken) {
            // Likely aborted/canceled requests after logout
            return throwError(err);
          }
          if (err.status === 401 && !request.url.includes('/auth/login')) {
            if (!isOnPublicRoute(this.router)) {
              this.notificationService.showErrorMessage(
                this.translateService.instant(i18n.main.api.session_expired),
              );
            }
            return throwError(err);
          }
          if (err.status === 400) {
            const errorKey = err.error?.errorKey;
            let errorMessage = errorKey
              ? this.translateService.instant(errorKey, err.error?.params)
              : null;
            if (!errorKey || errorMessage === errorKey) {
              errorMessage = this.translateService.instant(
                i18n.main.api.internal,
              );
            }
            this.notificationService.showErrorMessage(errorMessage);
          } else if (err.status === 500) {
            const timestamp = err.error?.timestamp;
            const key = timestamp
              ? i18n.main.api.internal_with_timestamp
              : i18n.main.api.internal;
            this.translateService
              .get(key, { timestamp })
              .subscribe((message) => {
                this.notificationService.showErrorMessage(message);
              });
          } else if (err.status === 403) {
            this.notificationService.showErrorMessage(
              this.translateService.instant(i18n.main.api.forbidden),
            );
          } else if (err.status === 429) {
            this.notificationService.showErrorMessage(
              this.translateService.instant(i18n.main.api.too_many_requests),
            );
          } else if (!this.authService.jwtToken) {
            // Avoid noisy toasts after session cleanup
            return throwError(err);
          } else {
            this.notificationService.showErrorMessage(
              this.translateService.instant('Unknown Error'),
            );
          }
        } else {
          if (!this.authService.jwtToken) {
            return throwError(err);
          }
          this.notificationService.showErrorMessage('Unknown Error');
        }

        return throwError(err);
      }),
    );
  }
}
